import React from "react";
import {Page, Text, View, Document, StyleSheet, Font, Image} from "@react-pdf/renderer";
import logoTpsWithAddress from "../../../../../statics/logos/logoTpsWithAddress.jpg";
import OpenSansRegular from "../../../../../statics/typos/openSansRegular.ttf";
import OpenSansSemiBold from "../../../../../statics/typos/openSansSemiBold.ttf";
import OpenSansBold from "../../../../../statics/typos/openSansBold.ttf";
import {GeneralDeclarationDataForFileModel} from "../../../../models/generalDeclaration";
import {DateUtils} from "../../../../../helpers/dateUtils";
import {Utils} from "../../../../../helpers/utils";
import { DirectionType } from "../../../../../common/models/enums";

Font.register({
    family: "OpenSans",
    format: "truetype",
    src: OpenSansRegular
});
Font.register({
    family: "OpenSansSemiBold",
    format: "truetype",
    src: OpenSansSemiBold
});
Font.register({
    family: "OpenSansBold",
    format: "truetype",
    src: OpenSansBold
});

const styles = StyleSheet.create({
    page: {
        paddingTop: 10,
        paddingLeft: 20,
        paddingRight: 20,
        flexDirection: "row",
        backgroundColor: "#FFF",
        fontFamily: "OpenSans"
    },
    viewer: {
        width: window.innerWidth,
        height: window.innerHeight,
    },
    header: {
        width: "100%",
        flexDirection: "row",
        fontSize: 7
    },
    headerImo: {
        width: 200
    },
    headerImage: {
        height: 37,
        marginLeft: 50
    },
    headerAttachment: {
        width: 150,
        marginLeft: 120
    },
    title: {
        marginTop: 10,
        marginRight: "auto",
        marginBottom: 15,
        marginLeft: "auto",
        fontSize: 13,
        weight: 700,
        textAlign: "center",
        fontFamily: "OpenSansBold"
    },
    table: {
        borderStyle: "solid",
        borderColor: "#000",
        borderWidth: 1,
        borderRightWidth: 0,
        borderBottomWidth: 0
    },
    tableRow: {
        margin: "auto",
        flexDirection: "row"
    },
    tableCol: {
        minHeight: 50,
        width: "50%",
        borderStyle: "solid",
        borderColor: "#000",
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0
    },
    tableSmallCol: {
        minHeight: 50,
        width: "25%",
        borderStyle: "solid",
        borderColor: "#000",
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0
    },
    tableBigCol: {
        minHeight: 50,
        width: "100%",
        borderStyle: "solid",
        borderColor: "#000",
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0
    },
    tableCellHeader: {
        marginLeft: 5,
        marginTop: 2,
        marginBottom: 5,
        fontSize: 8
    },
    tableCellHeaderInline: {
        marginRight: 20,
        marginBottom: 10
    },
    tableCellData: {
        marginLeft: 5,
        marginRight: 5,
        marginBottom: 5,
        fontSize: 10,
        fontFamily: "OpenSansSemiBold",
        textAlign: "center"
    },
    checkbox: {
        background: "#fff",
        color: "#000",
        width: 20,
        height: 20,
        borderStyle: "solid",
        borderColor: "#000",
        borderWidth: 1,
        fontSize: 13,
        fontFamily: "OpenSansSemiBold",
        textAlign: "center"
    }
});

interface GeneralDeclarationFileProps{
    generalDeclaration: GeneralDeclarationDataForFileModel | null | undefined
}

export default function GeneralDeclarationFile(props: GeneralDeclarationFileProps): React.ReactElement {
    if (!props.generalDeclaration) {
        return <Document><Page/></Document>;
    }

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View>
                    {renderHeader()}
                    {renderTable()}
                    {renderFooter()}
                </View>
            </Page>
        </Document>
    );

    function renderHeader() {
        return (
            <>
                <View style={styles.header}>
                    <View style={styles.headerImo}>
                        <Text>
                            Konwencja IMO o ułatwieniu{"\n"}
                            międzynarodowego obrotu morskiego / IMO{"\n"}
                            Convention on facilitation of International{"\n"}
                            Maritime Traffic
                        </Text>
                    </View>
                    <Image style={styles.headerImage} src={logoTpsWithAddress}/>
                    <View style={styles.headerAttachment}>
                        <Text>
                            Załącznik Nr 1 do Zarządzenia Nr 6{"\n"}
                            Dyrektora Urzędu Morskiego w{"\n"}
                            Szczecinie z dnia 29 grudnia 2004 r.
                        </Text>
                    </View>
                </View>
                <View style={styles.title}>
                    <Text>
                        ZGŁOSZENIE OGÓLNE GENERALNA DEKLARACJA{"\n"}
                        IMO GENERAL DECLARATION
                    </Text>
                </View>
            </>
        );
    }

    function renderDirectionType(directionType: DirectionType, expectedDirectionType: DirectionType) {
        return <Text style={[styles.checkbox, {marginTop: 5}]}>
            {Utils.renderBooleanValueInFile(directionType === expectedDirectionType)}
        </Text>;
    }

    function renderCrewOrPassengerList(hasList: boolean) {
        return <Text style={{marginTop: 5}}>
            {Utils.renderBooleanValueInFile(hasList)}
        </Text>;
    }

    function renderTable() {
        return (
            <View style={styles.table}>
                <View style={styles.tableRow}>
                    <View style={[styles.tableCol, {flexDirection: "row", minHeight: "auto"}]}>
                        <Text style={[styles.tableCellHeader, styles.tableCellHeaderInline, {fontSize: 9}]}>
                            Przybycie /{"\n"}
                            Arrival
                        </Text>
                        {renderDirectionType(props.generalDeclaration!.directionType, DirectionType.ARRIVAL)}
                    </View>
                    <View style={[styles.tableCol, {flexDirection: "row", minHeight: "auto"}]}>
                        <Text style={[styles.tableCellHeader, styles.tableCellHeaderInline, {fontSize: 9}]}>
                            Wyjście /{"\n"}
                            Departure
                        </Text>
                        {renderDirectionType(props.generalDeclaration!.directionType, DirectionType.DEPARTURE)}
                    </View>
                </View>
                <View style={styles.tableRow}>
                    <View style={styles.tableCol}>
                        <Text style={styles.tableCellHeader}>
                            1. Nazwa i rodzaj statku /{"\n"}
                            Name and description of ship
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.generalDeclaration!.shipNameAndDescription}
                        </Text>
                    </View>
                    <View style={[styles.tableSmallCol]}>
                        <Text style={styles.tableCellHeader}>
                            1.2 Numer IMO /{"\n"}
                            IMO number
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.generalDeclaration!.imoNo}
                        </Text>
                    </View>
                    <View style={[styles.tableSmallCol]}>
                        <Text style={styles.tableCellHeader}>
                            1.3 Sygnał wywoławczy /{"\n"}
                            Call sign
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.generalDeclaration!.callSign}
                        </Text>
                    </View>
                </View>
                <View style={styles.tableRow}>
                    <View style={styles.tableCol}>
                        <Text style={styles.tableCellHeader}>
                            1.4 Numer podrózy /{"\n"}
                            Voyage number
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.generalDeclaration!.voyageNo}
                        </Text>
                    </View>
                    <View style={[styles.tableSmallCol]}>
                        <Text style={styles.tableCellHeader}>
                            2. Port przybycia / wyjścia /{"\n"}
                            Port of arrival / departure
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.generalDeclaration!.arrivalDeparturePort}
                        </Text>
                    </View>
                    <View style={[styles.tableSmallCol]}>
                        <Text style={styles.tableCellHeader}>
                            3.Data–czas przybycia / wyjścia /{"\n"}
                            Date–time of arrival / departure
                        </Text>
                        <Text style={styles.tableCellData}>
                            {DateUtils.getFormattedDateWithTime(props.generalDeclaration!.arrivalDepartureTime)}
                        </Text>
                    </View>
                </View>
                <View style={styles.tableRow}>
                    <View style={styles.tableCol}>
                        <Text style={styles.tableCellHeader}>
                            4. Bandera /{"\n"}
                            Nationality of ship
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.generalDeclaration!.flagStateName}
                        </Text>
                    </View>
                    <View style={[styles.tableCol]}>
                        <Text style={styles.tableCellHeader}>
                            5. Port skąd przybył / port przeznaczenia /{"\n"}
                            Port arrived from / port of destination
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.generalDeclaration!.originDestinationPort}
                        </Text>
                    </View>
                </View>
                <View style={styles.tableRow}>
                    <View style={styles.tableCol}>
                        <View style={styles.tableRow}>
                            <View style={[styles.tableBigCol, {borderRightWidth: 0}]}>
                                <Text style={styles.tableCellHeader}>
                                    6. Certyfikat okrętowy (port; data; numer) /{"\n"}
                                    Certificate of registry (port; date; number)
                                </Text>
                                <Text style={styles.tableCellData}>
                                    {props.generalDeclaration!.registryCertificate}
                                </Text>
                            </View>
                        </View>
                        <View style={styles.tableRow}>
                            <View style={[styles.tableBigCol, {borderRightWidth: 0, borderBottomWidth: 0}]}>
                                <Text style={styles.tableCellHeader}>
                                    8. Tonaż brutto /{"\n"}
                                    Gross tonnage
                                </Text>
                                <Text style={styles.tableCellData}>
                                    {props.generalDeclaration!.grossTonnage}
                                </Text>
                            </View>
                        </View>
                    </View>
                    <View style={[styles.tableCol]}>
                        <Text style={styles.tableCellHeader}>
                            7. Nazwa i adres agenta statku /{"\n"}
                            Name and address of ship’s agent
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.generalDeclaration!.nameAndAddressShipAgent}
                        </Text>
                    </View>
                </View>
                <View style={styles.tableRow}>
                    <View style={styles.tableBigCol}>
                        <Text style={styles.tableCellHeader}>
                            9. Pozycja statku w porcie (nabrzeże lub pozycja) /{"\n"}
                            Position of the ship in the port (berth or station)
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.generalDeclaration!.shipPosition}
                        </Text>
                    </View>
                </View>
                <View style={styles.tableRow}>
                    <View style={styles.tableBigCol}>
                        <Text style={styles.tableCellHeader}>
                            10. Krótkie dane dotyczące podróży (poprzednie i kolejne porty zawinięcia, podkreślić gdzie
                            pozostały ładunek będzie wyładowany) /{"\n"}
                            Brief particulars of voyage (previous and subsequent ports of call; underline where
                            remaining cargo will be discharged)
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.generalDeclaration!.voyageDescription}
                        </Text>
                    </View>
                </View>
                <View style={styles.tableRow}>
                    <View style={styles.tableBigCol}>
                        <Text style={styles.tableCellHeader}>
                            11. Krótki opis ładunku /{"\n"}
                            Brief description of the cargo
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.generalDeclaration!.cargoDescription}
                        </Text>
                    </View>
                </View>
                <View style={styles.tableRow}>
                    <View style={styles.tableSmallCol}>
                        <Text style={styles.tableCellHeader}>
                            12. Ilość załogi (z kapitanem) /{"\n"}
                            Number of crew (incl. master)
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.generalDeclaration!.crewNumber}
                        </Text>
                    </View>
                    <View style={styles.tableSmallCol}>
                        <Text style={styles.tableCellHeader}>
                            13. Liczba pasażerów /{"\n"}
                            Number of passengers
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.generalDeclaration!.passengersNumber}
                        </Text>
                    </View>
                    <View style={styles.tableCol}>
                        <Text style={styles.tableCellHeader}>
                            14. Uwagi / {"\n"}
                            Remarks
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.generalDeclaration!.remarks}
                        </Text>
                    </View>
                </View>
                <View style={styles.tableRow}>
                    <View style={styles.tableSmallCol}>
                        <Text style={styles.tableCellHeader}>
                            15.Deklaracja ładunkowa (**) /{"\n"}
                            Cargo declaration (**)
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.generalDeclaration!.cargoDeclaration}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: "75%"}]}>
                        <Text style={styles.tableCellHeader}>
                            16. Zgłoszenie zasobów statku (**) /{"\n"}
                            Ship’s stores declaration (**)
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.generalDeclaration!.shipStoresDeclaration}
                        </Text>
                    </View>
                </View>
                <View style={styles.tableRow}>
                    <View style={styles.tableSmallCol}>
                        <Text style={styles.tableCellHeader}>
                            17. Lista załogi /{"\n"}
                            Crew list
                        </Text>
                        <Text style={styles.tableCellData}>
                            {renderCrewOrPassengerList(props.generalDeclaration!.crewList)}
                        </Text>
                    </View>
                    <View style={styles.tableSmallCol}>
                        <Text style={styles.tableCellHeader}>
                            18. Lista pasażerów /{"\n"}
                            Passenger list
                        </Text>
                        <Text style={styles.tableCellData}>
                            {renderCrewOrPassengerList(props.generalDeclaration!.passengerList)}
                        </Text>
                    </View>
                    <View style={styles.tableCol}>
                        <Text style={styles.tableCellHeader}>
                            19. Morska deklaracja zdrowia (*) /{"\n"}
                            Maritime declaration of health (*)
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.generalDeclaration!.maritimeDeclarationOfHealth}
                        </Text>
                    </View>
                </View>
                <View style={styles.tableRow}>
                    <View style={styles.tableBigCol}>
                        <Text style={styles.tableCellHeader}>
                            20. Data i podpis kapitana, upoważnionego agenta lub oficera /{"\n"}
                            Date and signature by master, authorized agent or officer
                        </Text>
                        <Text style={styles.tableCellData}>
                            {props.generalDeclaration!.dateAndSign}
                        </Text>
                    </View>
                </View>
            </View>
        );
    }

    function renderFooter() {
        return (
            <View style={{fontSize: 9}}>
                <View style={{fontSize: 8, marginBottom: 5, flexDirection: "row", alignItems: "flex-end"}}>
                    <Text>
                        Do użytku służbowego /{"\n"}
                        For official use
                    </Text>
                    <Text style={{marginLeft: 15}}>
                        Formularz IMO FAL Form 1
                    </Text>
                </View>
                <View style={{flexDirection: "row", alignItems: "flex-end"}}>
                    <View style={{flexDirection: "row", marginRight: 120}}>
                        <Text style={{marginRight: 25}}>
                            (*) tylko przybycia
                        </Text>
                        <Text>
                            {"\t"}(*) only arrival
                        </Text>
                    </View>
                    <View style={{flexDirection: "row"}}>
                        <Text style={{width: 130, marginRight: 20}}>
                            (**) dotyczy służb innych niż administracja morska
                        </Text>
                        <Text style={{width: 130}}>
                            (**) for services other than maritime administration
                        </Text>
                    </View>
                </View>
            </View>
        );
    }
}

